import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, ShieldQuestion, UsersRound, MailOpen, Clock, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { settingsApi, authApi } from '../lib/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import SignedInNow from './security/SignedInNow';
import SignInHistory from './security/SignInHistory';
import SecurityHealth from './security/SecurityHealth';

/**
 * Settings → Security. DESIGN-signing-in.md decisions G1–G10, H and H-ter.
 *
 * Its own file rather than another render function in SettingsPage.js, which is
 * already over 4,000 lines.
 *
 * The shape Rob agreed (designed 18/08/2026, mock-up approved 18/08/2026):
 *   - His own 2FA status at the top, with a recommendation for admins (G2 —
 *     recommended, NEVER forced). The "Set it up" button goes to /profile, where
 *     enrolment lives — one flow, two doors to it, no second copy.
 *   - Three separate 2FA requirement toggles (G7), each with its group's adoption
 *     count ("1 of 3 have set it up").
 *   - The grace period shown as fixed text, not a setting — Rob's call: seven days,
 *     no extra knob.
 *   - Two sign-in code switches: employees (defaults ON, G9) and managers
 *     (defaults OFF, H-ter). ⚠️ There is deliberately NO admin switch — decision H
 *     is not a setting, and a guard test enforces that server-side.
 */
const SecuritySettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [adoption, setAdoption] = useState(null);
  const [graceDays, setGraceDays] = useState(7);
  const [form, setForm] = useState({
    require_2fa_admins: false,
    require_2fa_managers: false,
    require_2fa_employees: false,
    allow_employee_signin_codes: true,
    allow_manager_signin_codes: false,
    allow_calendar_feeds: true,
    department_feed_audience: 'everyone',
    company_feed_audience: 'everyone',
  });
  const [myTwoFactor, setMyTwoFactor] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [settings, mine] = await Promise.all([
          settingsApi.getSecuritySettings(),
          authApi.twofactorStatus(),
        ]);
        const d = settings.data;
        setForm({
          require_2fa_admins: d.require_2fa_admins,
          require_2fa_managers: d.require_2fa_managers,
          require_2fa_employees: d.require_2fa_employees,
          allow_employee_signin_codes: d.allow_employee_signin_codes,
          allow_manager_signin_codes: d.allow_manager_signin_codes,
          // ⚠️ `?? true`, not `||`. An older backend that has not been restarted omits
          // this field entirely, and `undefined` handed to a controlled switch makes it
          // uncontrolled — React then warns and the toggle silently stops working. The
          // default matches the server's own fallback.
          allow_calendar_feeds: d.allow_calendar_feeds ?? true,
          // ⚠️ Same reasoning as the line above, and the fallback matters more here:
          // 'everyone' is what the feeds actually do when the column is unset, so any
          // other default would show a restriction that is not being applied.
          department_feed_audience: d.department_feed_audience ?? 'everyone',
          company_feed_audience: d.company_feed_audience ?? 'everyone',
        });
        setAdoption(d.adoption || null);
        setGraceDays(d.twofactor_grace_days ?? 7);
        setMyTwoFactor(mine.data);
      } catch (error) {
        // ⚠️ Say so rather than rendering every switch "off" — a failed read shown as
        // defaults would invite somebody to "fix" settings that are actually fine.
        setLoadFailed(true);
        toast.error("Couldn't load your security settings. Please refresh.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await settingsApi.updateSecuritySettings(form);
      setAdoption(data.adoption || null);
      toast.success('Security settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const count = (group) => {
    const a = adoption?.[group];
    if (!a) return null;
    return `${a.enrolled} of ${a.total} have set it up`;
  };

  /*
   * ⚠️ A plain `<select>`, not the Shadcn Select used elsewhere on this screen's cousins.
   * Three fixed options with no search, no async loading and no multi-select — the native
   * control is keyboard-accessible and screen-reader-correct for free, and this is not a
   * place to spend complexity.
   */
  const AUDIENCES = [
    ['everyone', 'Everyone'],
    ['managers', 'Managers and admins'],
    ['admins', 'Admins only'],
  ];

  const audienceRow = (key, label, sub, testId) => (
    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
      <div>
        <p className="font-medium">{label}</p>
        {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
      </div>
      <select
        className="h-10 w-52 shrink-0 rounded-md border border-border bg-card px-3 text-sm"
        value={form[key]}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        data-testid={testId}
      >
        {AUDIENCES.map(([value, text]) => (
          <option key={value} value={value}>{text}</option>
        ))}
      </select>
    </div>
  );

  const toggleRow = (key, label, sub, testId) => (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
      <div>
        <p className="font-medium">{label}</p>
        {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
      </div>
      <Switch
        checked={form[key]}
        onCheckedChange={(checked) => setForm((prev) => ({ ...prev, [key]: checked }))}
        data-testid={testId}
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="card-hive p-6">
        <p className="text-sm text-muted-foreground">
          Couldn't load your security settings. Please refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Your own second factor — the recommendation, never a requirement (G2) */}
      <div className="card-hive p-6" data-testid="security-own-2fa">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            myTwoFactor?.enrolled ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600'
          }`}>
            {myTwoFactor?.enrolled ? <ShieldCheck className="w-5 h-5" /> : <ShieldQuestion className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">Your two-factor authentication</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {myTwoFactor?.enrolled
                ? (myTwoFactor.method === 'totp'
                    ? "It's on — you use an authenticator app."
                    : "It's on — we email you a code when you sign in.")
                : "You haven't set it up yet. As an administrator you can see everyone's leave " +
                  "and sickness records, so we recommend it — but it's your choice."}
            </p>
          </div>
          <Button
            variant={myTwoFactor?.enrolled ? 'outline' : undefined}
            className={myTwoFactor?.enrolled ? 'flex-shrink-0' : 'btn-primary flex-shrink-0'}
            onClick={() => navigate('/profile')}
            data-testid="security-goto-profile"
          >
            {myTwoFactor?.enrolled ? 'Manage' : 'Set it up'}
          </Button>
        </div>
      </div>

      {/* The three per-role requirements (G7), with adoption counts */}
      <div className="card-hive p-6" data-testid="security-requirements">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <UsersRound className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold">Require two-factor authentication</h3>
            <p className="text-sm text-muted-foreground">
              Choose who must use a second step when they sign in
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {toggleRow('require_2fa_admins', 'Administrators', count('admins'), 'require-2fa-admins')}
          {toggleRow('require_2fa_managers', 'Managers', count('managers'), 'require-2fa-managers')}
          {toggleRow('require_2fa_employees', 'Employees', count('employees'), 'require-2fa-employees')}
        </div>

        {/* ⚠️ Fixed text, not a setting — Rob's call. The number still comes from the
            server so this line can never disagree with what sign-in enforces. */}
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-muted/50">
          <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            When you switch a requirement on, people get{' '}
            <span className="font-medium text-foreground">{graceDays} days</span> to set up.
            We'll remind them each time they sign in — after that, they can't sign in until
            they've done it.
          </p>
        </div>

        {/* ⚠️ The 3A line (G8-bis): the requirement applies to the PASSWORD door only, and
            saying so is the deal — the customer weighs it, we don't decide for them.
            Shown only when it's actually true of their setup. */}
        {form.require_2fa_employees && form.allow_employee_signin_codes && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-muted/50"
               data-testid="employee-codes-note">
            <MailOpen className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              Employees who sign in with a code aren't affected by this — their emailed
              code already proves they own the mailbox. If you want everyone on a password
              plus a second step, switch off sign-in codes below.
            </p>
          </div>
        )}
      </div>

      {/* Sign-in codes (G9 + H-ter). No admin switch, deliberately (H). */}
      <div className="card-hive p-6" data-testid="security-signin-codes">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <MailOpen className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold">Sign-in codes</h3>
            <p className="text-sm text-muted-foreground">
              A one-time emailed code instead of a password. Handy for people who rarely
              sign in. Administrators always use a password.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {toggleRow('allow_employee_signin_codes', 'Employees can sign in with a code',
            'On unless you switch it off', 'allow-employee-codes')}
          {toggleRow('allow_manager_signin_codes', 'Managers can sign in with a code',
            'Off unless you decide managers should have it too', 'allow-manager-codes')}
        </div>
      </div>

      {/* Calendar feeds (ROADMAP §7). Read-only links, so this is a disclosure control
          rather than an access one — hence the default being on. */}
      <div className="card-hive p-6" data-testid="security-calendar-feeds">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h3 className="font-semibold">Calendar feeds</h3>
            <p className="text-sm text-muted-foreground">
              People can add their leave, and who's off, to Google Calendar, Outlook or
              Apple Calendar. Each person gets a private link from their own profile.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {toggleRow('allow_calendar_feeds', 'Allow calendar feeds',
            'On unless you switch it off', 'allow-calendar-feeds')}
        </div>

        {!form.allow_calendar_feeds && (
          <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-sm text-muted-foreground">
              Switching this off stops every link that has already been shared, straight
              away. Anyone using one will see their HolidayHive calendar stop updating.
            </p>
          </div>
        )}

        {/*
          * Who may subscribe to a feed of OTHER PEOPLE'S leave.
          *
          * ⚠️ Hidden entirely when the master switch is off — there is nothing to scope
          * when nobody can subscribe to anything, and two live-looking dropdowns under a
          * disabled feature invite an admin to set a policy that is doing nothing.
          */}
        {form.allow_calendar_feeds && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="font-medium">Who can subscribe to colleagues' leave</p>
            <p className="text-sm text-muted-foreground mb-4">
              {/*
                * ⚠️ Says plainly that own leave is never affected. Without it an admin
                * choosing "Admins only" has every reason to think they are switching the
                * whole feature off for their staff, and the most likely outcome is that
                * they leave it alone rather than risk it.
                */}
              Everyone can always subscribe to their own leave. These two links carry other
              people's.
            </p>

            <div className="space-y-3">
              {audienceRow(
                'department_feed_audience',
                "Who's off in your department",
                'People in the same department',
                'department-feed-audience',
              )}
              {audienceRow(
                'company_feed_audience',
                "Who's off across the company",
                'Everybody, in every department',
                'company-feed-audience',
              )}
            </div>

            {/*
              * ⚠️ ALWAYS SHOWN, not only when something is restricted. The first sentence
              * is why an admin would narrow this at all, and it is exactly as true when
              * the answer is "Everyone" — that is the setting most likely to want a second
              * thought, so hiding the warning behind a change would be backwards.
              */}
            <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-muted-foreground">
                These links work without signing in, so anyone they are forwarded to can
                read them. Narrowing this stops existing links straight away.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button className="btn-primary" onClick={save} disabled={saving} data-testid="security-save">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Settings → Security, in three tabs (DESIGN-security.md, Rob's answer 1A, 24/10/2026):
 *   Overview — the security health card (step 2, 25/10/2026), then who's signed in now;
 *   Sign-ins — the company's history, filters and export;
 *   Settings — the four cards above, exactly as they were.
 * Admins only, like the rest of Settings → Security (2A).
 */
const SecurityTab = () => (
  <Tabs defaultValue="overview" className="space-y-4">
    <TabsList>
      <TabsTrigger value="overview" data-testid="security-tab-overview">Overview</TabsTrigger>
      <TabsTrigger value="signins" data-testid="security-tab-signins">Sign-ins</TabsTrigger>
      <TabsTrigger value="settings" data-testid="security-tab-settings">Settings</TabsTrigger>
    </TabsList>
    <TabsContent value="overview"><SecurityHealth /><SignedInNow /></TabsContent>
    <TabsContent value="signins"><SignInHistory /></TabsContent>
    <TabsContent value="settings"><SecuritySettings /></TabsContent>
  </Tabs>
);

export default SecurityTab;
